/* Portal Campaign templates — 5 Premium variants rendered on the public portal.
 * STAS picks the template + assets via the admin UI; this file owns the visual identity.
 * Strict design rules: no AI-slop gradients, gold accent, calm motion, mobile-first.
 *
 * Templates: banner | card | video_card | popup | carousel_item
 */
import { useEffect, useState } from "react";
import { ArrowRight, Play, X } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const pick = (campaign, ar, en, lang) => {
  if (lang === "ar") return campaign[ar] || campaign[en] || "";
  return campaign[en] || campaign[ar] || "";
};

const fullUrl = (relPath) => (relPath ? `${API_BASE}${relPath}` : null);

/**
 * Returns the list of image URLs for a campaign. Multi-image slideshow takes
 * priority over single image_url. Falls back to [single] if only image_url set.
 */
function imageListOf(campaign) {
  if (campaign.image_urls && campaign.image_urls.length) {
    return campaign.image_urls.map(fullUrl);
  }
  if (campaign.image_url) {
    return [fullUrl(campaign.image_url)];
  }
  return [];
}

/**
 * 16:9 image frame. If the campaign has more than one image, auto-rotates with
 * a slide transition. STAS controls the interval (seconds) and direction.
 * Pauses on hover so users can read longer slides.
 */
function CampaignImageFrame({ campaign, theme, alt = "" }) {
  const imgs = imageListOf(campaign);
  const interval = Math.max(1, Math.min(300, Number(campaign.slide_interval_seconds) || 5));
  const directionRtl = (campaign.slide_direction || "rtl") === "rtl";
  const fit = campaign.image_fit === "cover" ? "cover" : "contain";

  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (imgs.length <= 1 || paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % imgs.length), interval * 1000);
    return () => clearInterval(t);
  }, [imgs.length, paused, interval]);

  if (!imgs.length) return null;
  const bg = theme.name === "dark" ? "#0a0a0a" : "#f4f0e3";

  return (
    <div
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      data-testid="campaign-image-frame"
      style={{
        width: "100%", aspectRatio: "16/9",
        background: fit === "contain" ? bg : "transparent",
        position: "relative", overflow: "hidden",
      }}
    >
      {imgs.map((src, i) => {
        const isActive = i === idx;
        // Off-screen slides sit at +100% (next direction) or −100% (prev).
        // Direction "rtl" means new slide enters from the right (translateX(+100%)).
        const offsetSign = directionRtl ? 1 : -1;
        const offBase = isActive ? "0%" : (i === (idx + 1) % imgs.length ? `${offsetSign * 100}%` : `${-offsetSign * 100}%`);
        return (
          <img
            key={i} src={src} alt={alt}
            style={{
              position: "absolute", inset: 0,
              width: "100%", height: "100%", objectFit: fit,
              transform: `translateX(${offBase})`,
              transition: "transform 700ms cubic-bezier(0.4, 0.0, 0.2, 1), opacity 500ms ease",
              opacity: isActive ? 1 : 0,
            }}
          />
        );
      })}
      {imgs.length > 1 && (
        <div style={{
          position: "absolute", bottom: 8, left: 0, right: 0,
          display: "flex", justifyContent: "center", gap: 5, zIndex: 2,
        }}>
          {imgs.map((_, i) => (
            <span key={i} data-testid={`campaign-slide-dot-${i}`} style={{
              width: i === idx ? 18 : 6, height: 6, borderRadius: 999,
              background: i === idx ? "#fff" : "rgba(255,255,255,0.5)",
              transition: "width 300ms ease, background 300ms ease",
              boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
            }} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Tiny helper that returns a <video> src — prefers external URL, falls back to backend asset. */
function videoSrcOf(c) {
  if (c.video_external_url) return c.video_external_url;
  if (c.video_url) return fullUrl(c.video_url);
  return null;
}

function CtaButton({ campaign, lang, theme }) {
  const label = pick(campaign, "cta_label_ar", "cta_label_en", lang);
  if (!label || !campaign.cta_url) return null;
  return (
    <a
      href={campaign.cta_url} target="_blank" rel="noopener noreferrer"
      data-testid={`campaign-cta-${campaign.id}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        padding: "11px 22px", borderRadius: 999,
        background: theme.goldGradient, color: theme.name === "dark" ? "#0a0a0a" : "#fff",
        fontWeight: 900, fontSize: 13, textDecoration: "none",
        boxShadow: "0 8px 20px rgba(212,175,55,0.25)",
      }}
    >
      <span>{label}</span>
      <ArrowRight size={14} strokeWidth={2.6} style={{ transform: lang === "ar" ? "scaleX(-1)" : "none" }} />
    </a>
  );
}

/* ───────────────────────── Banner ─────────────────────────
 * 16:9 ad-screen frame. Image OR video fills the frame using the chosen fit
 * (contain by default = no crop). Title/body OPTIONAL — if both are empty the
 * frame stays clean (image-only ad). Standard dimensions: 1200 × 675 px.
 */
function BannerTemplate({ campaign, lang, theme, dir }) {
  const title = pick(campaign, "title_ar", "title_en", lang);
  const body = pick(campaign, "body_ar", "body_en", lang);
  const imgs = imageListOf(campaign);
  const hasText = !!(title || body || campaign.cta_url);
  return (
    <div
      dir={dir} data-testid={`campaign-banner-${campaign.id}`}
      style={{
        width: "100%", maxWidth: 880, marginInline: "auto", marginBottom: 24,
        borderRadius: 18, overflow: "hidden",
        background: theme.surfaceCard,
        border: `1px solid ${theme.borderAccent}`,
        boxShadow: theme.cardShadow,
      }}
    >
      {imgs.length > 0 && <CampaignImageFrame campaign={campaign} theme={theme} alt={title} />}
      {hasText && (
        <div style={{ padding: 18, textAlign: lang === "en" ? "left" : "right" }}>
          {title && <div style={{ fontWeight: 900, fontSize: 17, color: theme.gold, marginBottom: 4 }}>{title}</div>}
          {body && <div style={{ fontSize: 13, color: theme.textSoft, lineHeight: 1.8, marginBottom: 12 }}>{body}</div>}
          <CtaButton campaign={campaign} lang={lang} theme={theme} />
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── Card ───────────────────────── */
function CardTemplate({ campaign, lang, theme, dir }) {
  const title = pick(campaign, "title_ar", "title_en", lang);
  const body = pick(campaign, "body_ar", "body_en", lang);
  const imgs = imageListOf(campaign);
  const hasText = !!(title || body || campaign.cta_url);
  return (
    <div
      dir={dir} data-testid={`campaign-card-${campaign.id}`}
      style={{
        width: "100%", maxWidth: 520, marginInline: "auto", marginBottom: 24,
        borderRadius: 18, overflow: "hidden",
        background: theme.surfaceCard,
        border: `1px solid ${theme.borderAccent}`,
        boxShadow: theme.cardShadow,
      }}
    >
      {imgs.length > 0 && <CampaignImageFrame campaign={campaign} theme={theme} alt={title} />}
      {hasText && (
        <div style={{ padding: 18, textAlign: lang === "en" ? "left" : "right" }}>
          {title && <div style={{ fontWeight: 900, fontSize: 17, color: theme.gold, marginBottom: 6 }}>{title}</div>}
          {body && <div style={{ fontSize: 13, color: theme.textSoft, lineHeight: 1.8, marginBottom: 14 }}>{body}</div>}
          <CtaButton campaign={campaign} lang={lang} theme={theme} />
        </div>
      )}
    </div>
  );
}

/* ──────────────────────── Video Card ────────────────────────
 * Inline player that loads only after the user taps Play.
 */
function VideoCardTemplate({ campaign, lang, theme, dir }) {
  const [playing, setPlaying] = useState(false);
  const title = pick(campaign, "title_ar", "title_en", lang);
  const body = pick(campaign, "body_ar", "body_en", lang);
  const poster = fullUrl(campaign.image_url);
  const src = videoSrcOf(campaign);
  return (
    <div
      dir={dir} data-testid={`campaign-video-${campaign.id}`}
      style={{
        width: "100%", maxWidth: 480, marginBottom: 24,
        borderRadius: 18, overflow: "hidden",
        background: theme.surfaceCard,
        border: `1px solid ${theme.borderAccent}`,
        boxShadow: theme.cardShadow,
      }}
    >
      <div style={{ position: "relative", width: "100%", aspectRatio: "16/9", background: "#000" }}>
        {playing && src ? (
          /youtube\.com|youtu\.be|vimeo\.com/.test(src) ? (
            <iframe
              src={src.replace("watch?v=", "embed/")}
              title={title}
              allow="autoplay; encrypted-media"
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          ) : (
            <video src={src} controls autoPlay playsInline style={{ width: "100%", height: "100%", objectFit: "contain", background: "#000" }} />
          )
        ) : (
          <>
            {poster && <img src={poster} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />}
            {src && (
              <button
                onClick={() => setPlaying(true)} data-testid={`campaign-video-play-${campaign.id}`}
                style={{
                  position: "absolute", inset: 0, display: "flex",
                  alignItems: "center", justifyContent: "center",
                  background: "rgba(0,0,0,0.35)", border: "none", cursor: "pointer",
                }}
              >
                <span style={{
                  width: 64, height: 64, borderRadius: "50%",
                  background: theme.goldGradient, display: "inline-flex",
                  alignItems: "center", justifyContent: "center",
                  boxShadow: "0 10px 30px rgba(212,175,55,0.5)",
                }}>
                  <Play size={26} strokeWidth={2.6} color={theme.name === "dark" ? "#0a0a0a" : "#fff"} fill="currentColor" />
                </span>
              </button>
            )}
          </>
        )}
      </div>
      {(title || body || campaign.cta_url) && (
        <div style={{ padding: 16, textAlign: lang === "en" ? "left" : "right" }}>
          {title && <div style={{ fontWeight: 900, fontSize: 16, color: theme.gold, marginBottom: 5 }}>{title}</div>}
          {body && <div style={{ fontSize: 13, color: theme.textSoft, lineHeight: 1.7, marginBottom: 12 }}>{body}</div>}
          <CtaButton campaign={campaign} lang={lang} theme={theme} />
        </div>
      )}
    </div>
  );
}

/* ──────────────────────── Popup ────────────────────────
 * Calm modal that fades in once after 1 s and is dismissible.
 * Remembers dismissal in sessionStorage so it never spams.
 */
function PopupTemplate({ campaign, lang, theme, dir }) {
  const [open, setOpen] = useState(false);
  const dismissKey = `vta_popup_dismissed_${campaign.id}`;
  useEffect(() => {
    if (sessionStorage.getItem(dismissKey)) return;
    const t = setTimeout(() => setOpen(true), 1000);
    return () => clearTimeout(t);
  }, [dismissKey]);
  if (!open) return null;
  const title = pick(campaign, "title_ar", "title_en", lang);
  const body = pick(campaign, "body_ar", "body_en", lang);
  const close = () => {
    sessionStorage.setItem(dismissKey, "1");
    setOpen(false);
  };
  return (
    <div
      data-testid={`campaign-popup-${campaign.id}`} dir={dir}
      style={{
        position: "fixed", inset: 0, zIndex: 9000,
        background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16, animation: "fadeIn 200ms ease both",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div style={{
        width: "100%", maxWidth: 420,
        background: theme.surfaceCard,
        border: `1px solid ${theme.borderAccent}`,
        borderRadius: 22, overflow: "hidden",
        boxShadow: "0 25px 70px rgba(0,0,0,0.5)",
        position: "relative",
      }}>
        <button
          onClick={close} data-testid={`campaign-popup-close-${campaign.id}`}
          aria-label="Close" style={{
            position: "absolute", top: 10, [lang === "en" ? "right" : "left"]: 10,
            width: 32, height: 32, borderRadius: "50%",
            background: "rgba(0,0,0,0.45)", border: "none", color: "#fff",
            cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center",
            zIndex: 2,
          }}
        ><X size={16} strokeWidth={2.6} /></button>
        {imageListOf(campaign).length > 0 && (
          <CampaignImageFrame campaign={campaign} theme={theme} alt={title} />
        )}
        {(title || body || campaign.cta_url) && (
          <div style={{ padding: 22, textAlign: lang === "en" ? "left" : "right" }}>
            {title && <div style={{ fontWeight: 900, fontSize: 18, color: theme.gold, marginBottom: 8 }}>{title}</div>}
            {body && <div style={{ fontSize: 13, color: theme.textSoft, lineHeight: 1.8, marginBottom: 14 }}>{body}</div>}
            <CtaButton campaign={campaign} lang={lang} theme={theme} />
          </div>
        )}
      </div>
    </div>
  );
}

/* ──────────────────────── Carousel ────────────────────────
 * Auto-advances every 6 s, pauseable. Used when multiple campaigns target the same slot.
 */
function CarouselTemplate({ campaigns, lang, theme, dir, renderItem }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused || campaigns.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % campaigns.length), 6000);
    return () => clearInterval(t);
  }, [paused, campaigns.length]);
  if (!campaigns.length) return null;
  return (
    <div
      data-testid="campaign-carousel" dir={dir}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      {renderItem(campaigns[idx])}
      {campaigns.length > 1 && (
        <div style={{ display: "flex", gap: 6, marginTop: -8, marginBottom: 22 }}>
          {campaigns.map((_, i) => (
            <button
              key={i} onClick={() => setIdx(i)} aria-label={`Slide ${i + 1}`}
              data-testid={`campaign-carousel-dot-${i}`}
              style={{
                width: i === idx ? 24 : 8, height: 8, borderRadius: 999,
                border: "none", cursor: "pointer",
                background: i === idx ? theme.gold : theme.borderSoft,
                transition: "width 200ms ease, background 200ms ease",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ──────────────────────── Dispatcher ────────────────────────
 * Picks the right template for a single campaign. Used by CampaignSlot for non-carousel items.
 */
function renderTemplate(campaign, { lang, theme, dir }) {
  const props = { campaign, lang, theme, dir };
  switch (campaign.template) {
    case "card": return <CardTemplate key={campaign.id} {...props} />;
    case "video_card": return <VideoCardTemplate key={campaign.id} {...props} />;
    case "popup": return <PopupTemplate key={campaign.id} {...props} />;
    case "banner":
    case "carousel_item":
    default: return <BannerTemplate key={campaign.id} {...props} />;
  }
}

/* ──────────────────────── Public exports ────────────────────────
 * <CampaignSlot placement="landing_top" /> — drop anywhere in the portal.
 * <CampaignPreview campaign={...} /> — used by the STAS admin preview pane.
 */
export function CampaignSlot({ placement, campaigns, lang, theme, dir }) {
  if (!campaigns || !campaigns.length) return null;
  const here = campaigns.filter((c) => c.placement === placement || c.placement === "all");
  if (!here.length) return null;
  // Popups always render alone (they are overlays — never carouselled).
  const popups = here.filter((c) => c.template === "popup");
  const inline = here.filter((c) => c.template !== "popup");
  return (
    <>
      {popups.map((c) => renderTemplate(c, { lang, theme, dir }))}
      {inline.length === 1 && renderTemplate(inline[0], { lang, theme, dir })}
      {inline.length > 1 && (
        <CarouselTemplate
          campaigns={inline} lang={lang} theme={theme} dir={dir}
          renderItem={(c) => renderTemplate(c, { lang, theme, dir })}
        />
      )}
    </>
  );
}

export function CampaignPreview({ campaign, lang, theme, dir }) {
  if (!campaign) return null;
  return renderTemplate(campaign, { lang, theme, dir });
}

/**
 * Portal-accurate mockup. Renders a scaled-down replica of the actual landing
 * page (logo, title, CTAs) and places the live preview of the unsaved campaign
 * at the chosen placement slot. STAS sees EXACTLY what a client will see.
 */
export function PortalMockup({ campaign, lang, theme, dir, branding, T, scale = 0.78 }) {
  if (!campaign) return null;
  const placement = campaign.placement || "landing_top";
  const slotEl = (
    <div style={{ display: "flex", justifyContent: "center", width: "100%", marginBlock: 12 }}>
      {renderTemplate(campaign, { lang, theme, dir })}
    </div>
  );
  const name = lang === "ar" ? branding?.app_name_ar : branding?.app_name_en;
  const tagline = lang === "ar" ? branding?.tagline_ar : branding?.tagline_en;
  return (
    <div
      dir={dir} data-testid="portal-mockup"
      style={{
        position: "relative", overflow: "hidden",
        background: theme.bg, color: theme.text,
        border: `1px solid ${theme.borderAccent}`, borderRadius: 16,
        padding: 24, fontFamily: "'Tajawal','Segoe UI',sans-serif",
        minHeight: 520,
        transform: `scale(${scale})`, transformOrigin: "top center",
        width: `${100 / scale}%`, marginInline: `-${(100 / scale - 100) / 2}%`,
      }}
    >
      {/* Golden glow — matches real landing */}
      <div style={{
        position: "absolute", top: "-12%", left: "50%",
        transform: "translateX(-50%)", width: 380, height: 380,
        background: `radial-gradient(circle, ${theme.gold}22, transparent 60%)`,
        pointerEvents: "none", zIndex: 0,
      }} />
      <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
        {placement === "landing_top" && slotEl}
        {branding?.logo_url && (
          <img src={branding.logo_url.startsWith("http") ? branding.logo_url : `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}`}
            alt="" style={{ width: 110, height: 110, objectFit: "contain", marginBottom: 12 }} />
        )}
        <h2 style={{ fontSize: 24, fontWeight: 900, color: theme.gold, margin: "0 0 8px", lineHeight: 1.3 }}>
          {(T && name && T.landing_title) ? T.landing_title(name) : (name || "VTA")}
        </h2>
        <p style={{ fontSize: 13, color: theme.textMuted, marginBottom: 24, maxWidth: 420 }}>
          {tagline || T?.landing_sub || ""}
        </p>
        {placement === "landing_middle" && slotEl}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center", marginBottom: 18 }}>
          <div style={{
            padding: "12px 22px", background: theme.goldGradient,
            color: theme.name === "dark" ? "#0a0a0a" : "#fff", borderRadius: 12,
            fontSize: 13, fontWeight: 800, minWidth: 160, textAlign: "center",
          }}>{T?.start || "ابدأ معاملتك"}</div>
          <div style={{
            padding: "12px 22px", background: "transparent", color: theme.text,
            border: `1.5px solid ${theme.borderAccent}`, borderRadius: 12,
            fontSize: 13, fontWeight: 800, minWidth: 160, textAlign: "center",
          }}>{T?.track || "تتبع معاملتك"}</div>
        </div>
        {placement === "landing_bottom" && slotEl}
        {(placement === "all" || placement === "submit_pre" || placement === "track_page") && slotEl}
      </div>
    </div>
  );
}

export const TEMPLATES = [
  { code: "banner", label_ar: "بانر أفقي", label_en: "Horizontal Banner" },
  { code: "card", label_ar: "بطاقة عمودية", label_en: "Vertical Card" },
  { code: "video_card", label_ar: "بطاقة فيديو", label_en: "Video Card" },
  { code: "popup", label_ar: "نافذة منبثقة هادئة", label_en: "Soft Popup" },
  { code: "carousel_item", label_ar: "عنصر داخل Carousel", label_en: "Carousel Item" },
];

export const PLACEMENTS = [
  { code: "landing_top", label_ar: "أعلى الواجهة الترحيبية", label_en: "Landing — top" },
  { code: "landing_middle", label_ar: "بين الشعار والأزرار", label_en: "Landing — middle" },
  { code: "landing_bottom", label_ar: "أسفل الأزرار", label_en: "Landing — below CTAs" },
  { code: "submit_pre", label_ar: "قبل بدء نموذج التقديم", label_en: "Before submit form" },
  { code: "track_page", label_ar: "صفحة التتبع", label_en: "Track page" },
  { code: "all", label_ar: "الجميع (كل البوابة)", label_en: "Everywhere" },
];
